import { useEffect, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";

import { apiClient, ApiError } from "../api/client";
import { useAuth } from "./AuthContext";

type Role = "user" | "manager" | "owner";

const navigation: Array<{ to: string; label: string; end: boolean; roles?: Role[] }> = [
  { to: "/", label: "工作台", end: true },
  { to: "/capabilities", label: "能力中心", end: false },
  { to: "/products", label: "商品管理", end: false },
  { to: "/listing-copywriting", label: "Listing 文案", end: false },
  { to: "/runs", label: "运行记录", end: false },
  { to: "/replenishment", label: "补货建议", end: false },
  { to: "/images", label: "图片试点", end: false },
  { to: "/datasets", label: "数据与产物", end: false },
  { to: "/account", label: "我的账户", end: false },
  { to: "/users", label: "用户管理", end: false, roles: ["manager", "owner"] },
  { to: "/departments", label: "部门管理", end: false, roles: ["owner"] },
  { to: "/admin", label: "平台管理", end: false, roles: ["manager", "owner"] },
];

export function AppShell() {
  const { logout, session } = useAuth();
  const [accountName, setAccountName] = useState<{ userId: string; value: string } | null>(null);
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (!session) return;
    let current = true;
    apiClient
      .getCurrentAccount()
      .then((profile) => {
        if (current) {
          setAccountName({ userId: session.user_id, value: profile.display_name });
        }
      })
      .catch(() => {
        if (current) {
          setAccountName({ userId: session.user_id, value: "当前用户" });
        }
      });
    return () => {
      current = false;
    };
  }, [session]);

  async function exitSession() {
    setLogoutError(null);
    setLoggingOut(true);
    try {
      await logout();
    } catch (caught) {
      setLogoutError(caught instanceof ApiError ? caught.message : "暂时无法安全退出");
      setLoggingOut(false);
    }
  }

  const role = session?.role ?? "user";
  const accountLabel =
    session && accountName?.userId === session.user_id ? accountName.value : "当前用户";
  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark" aria-hidden="true">
            EA
          </span>
          <div>
            <strong>EcomAssistant</strong>
            <span>企业 AI 工作台</span>
          </div>
        </div>

        <nav aria-label="主导航" className="primary-nav">
          {navigation
            .filter((item) => !item.roles || item.roles.includes(role))
            .map((item) => (
              <NavLink
                className={({ isActive }) => (isActive ? "nav-link active" : "nav-link")}
                end={item.end}
                key={item.to}
                to={item.to}
              >
                {item.label}
              </NavLink>
            ))}
        </nav>

        <div className="sidebar-footer">
          <span className="status-dot" aria-hidden="true" />
          <div>
            <strong>内网环境</strong>
            <span>模型调用由网关统一管理</span>
          </div>
        </div>
      </aside>

      <div className="content-column">
        <header className="topbar">
          <div>
            <span className="eyebrow">跨境电商 AI 提效平台</span>
            <strong>业务能力中心</strong>
          </div>
          <div className="profile-actions">
            {logoutError && (
              <span className="topbar-error" role="alert">
                {logoutError}
              </span>
            )}
            <div aria-label={`当前账号，${roleLabel(role)}`} className="profile-summary">
              <span aria-hidden="true">{roleInitial(role)}</span>
              <span className="profile-copy">
                <strong>{accountLabel}</strong>
                <small>{roleLabel(role)}</small>
              </span>
            </div>
            <button
              className="logout-button"
              disabled={loggingOut}
              onClick={() => void exitSession()}
              type="button"
            >
              {loggingOut ? "正在退出…" : "退出"}
            </button>
          </div>
        </header>

        <main className="main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function roleLabel(role: Role): string {
  return { user: "普通用户", manager: "管理员", owner: "所有者" }[role];
}

function roleInitial(role: Role): string {
  return { user: "U", manager: "M", owner: "O" }[role];
}
